import { WorkflowBlockType, WorkflowItem, WorkflowListParams, WorkflowType } from './workflowsSlice'
import {
  WorkflowFlowTypes,
  WorkflowFormData,
  WorkflowStateNotificationTypes
} from './views/createWorkflows/types'
import {
  convertBlocksToUARWorkflowFormData,
  convertBlocksToWorkflowFormData
} from './views/createWorkflows/constants'
import { GRAPHQL_API_FILTERS, LIMIT_DEFAULT } from '../../constants'
import { getAfterCursor, parameterizeArrayofObjects } from '../../utils/graphqlUtil'
import { gql } from 'graphql-request'

export const queryWorkflowsList = (params: WorkflowListParams): string => {
  const { page, filters } = params
  const cursor = getAfterCursor(page || 1, LIMIT_DEFAULT)

  let booleanFilter = ''
  if (filters?.booleanFilter?.length) {
    booleanFilter = `,booleanFilter: ${parameterizeArrayofObjects(filters.booleanFilter)}, `
  }

  let filter = ''
  if (filters?.filter?.length) {
    filter = `,filter: ${parameterizeArrayofObjects(filters.filter)}, `
  }

  return gql`
    {
      flow(first: ${LIMIT_DEFAULT}, after:"${cursor}" ${filter} ${booleanFilter}) {
        count
        edges {
          node {
            id
            name
            type
            createdAt
            createdBy
            attachedPolicyCount
          }
        }
      }
    }
  `
}

export const mapQueryWorkflowsList = (raw: any): { list: WorkflowItem[]; total: number } => {
  try {
    const list = raw.flow.edges.map(({ node }) => ({
      flowId: node?.id,
      name: node?.name || '',
      attachedPolicyCount: node?.attachedPolicyCount || 0,
      createdAt: node?.createdAt || '',
      createdBy: node?.createdBy || ''
    }))
    return { list, total: raw.flow?.count || 0 }
  } catch (error) {
    console.error(error)
    throw error
  }
}

const buildPolicyWorkflowBlocks = (data: WorkflowFormData): string => {
  const ruleSetData = data[WorkflowFlowTypes.RULE_SET]
  const alertData = data[WorkflowFlowTypes.ALERT]
  const stateData = data[WorkflowFlowTypes.STATE_NOTIFICATION]
  const stateApprovalData = stateData?.[WorkflowStateNotificationTypes.STATE_APPROVALS]
  const stateNotificationData = stateData?.[WorkflowStateNotificationTypes.STATE_NOTIFICATION]
  const stateCronExpression = stateData?.cronExpression
  const actionData = data[WorkflowFlowTypes.ACTION]

  const ruleSetBlocks = `{
      id: "${ruleSetData?.id}",
      order: 1,
      type: ${WorkflowBlockType.POLICY_UPSERT_NOTIFICATION},
      policyUpsertNotifications: {
        enabled: ${ruleSetData?.isEnabled},
        recipientGroups: [${ruleSetData?.recipients}],
        additionalRecipients: ${JSON.stringify(ruleSetData?.additionalNotificationRecipients)}
      }
    }`

  const alertBlocks = `{
      id: "${alertData?.id}",
      order: 2,
      type: ${WorkflowBlockType.POLICY_ALERT_ASSIGNMENT},
      policyAlertAssignments: {
        enabled: ${alertData?.isEnabled},
        assignmentGroup: ${alertData?.recipients},
        additionalRecipients: ${JSON.stringify(alertData?.additionalNotificationRecipients)}
      }
    }`
  const stateNotificationBlocks = `{
    id: "${stateNotificationData?.id}",
    order: 3,
    type: ${WorkflowBlockType.POLICY_STATE_NOTIFICATION},
    stateNotifications: {
      enabled: true,
      states: [${stateNotificationData?.states}],
      recipientGroups: [${stateNotificationData?.recipients}],
      additionalRecipients: ${JSON.stringify(
        stateNotificationData?.additionalNotificationRecipients
      )},
      cronExpression: "${stateCronExpression}"
    }
  }`
  const stateApprovalBlocks = `{
    id: "${stateApprovalData?.id}",
    order: 4,
    type: ${WorkflowBlockType.POLICY_STATE_APPROVAL},
    stateApprovals: {
      enabled: true,
      states: [${stateApprovalData?.states}],
      approverGroups: [${stateApprovalData?.recipients}],
      additionalApprovers: ${JSON.stringify(stateApprovalData?.additionalNotificationRecipients)},
      cronExpression: "${stateCronExpression}"
    }
  }`

  const actionBlocksConfigs = actionData?.actions
    ?.map(
      (action) => `{
    id: "${action?.id}",
    approvalEnabled: ${action?.approvalEnabled},
    notificationsEnabled: ${action?.notificationsEnabled},
    actionType: ${action?.actionType},
    notificationRecipientGroups: [${action?.notificationRecipientGroups}],
    additionalNotificationRecipients: ${JSON.stringify(action?.additionalNotificationRecipients)},
    approverGroups: [${action?.approverGroups}],
    additionalApprovers: ${JSON.stringify(action?.additionalApproverRecipients)}
  }`
    )
    .join(',')

  const actionBlocks = `{
    id: "${actionData?.id}",
    order: 5,
    type: ${WorkflowBlockType.POLICY_ACTION_NOTIFICATION_AND_APPROVAL},
    actionsNotificationsAndApprovals: {
    cronExpression: "${actionData?.cronExpression}",
      configs: [${actionBlocksConfigs}]
    }
  }`
  const blocks: string[] = []

  if (ruleSetData && Object.keys(ruleSetData).length > 0) {
    blocks.push(ruleSetBlocks)
  }
  if (alertData && Object.keys(alertData).length > 0) {
    blocks.push(alertBlocks)
  }

  if (stateNotificationData && Object.keys(stateNotificationData).length > 0) {
    blocks.push(stateNotificationBlocks)
  }

  if (stateApprovalData && Object.keys(stateApprovalData).length > 0) {
    blocks.push(stateApprovalBlocks)
  }

  if (actionData && actionData.actions && actionData.actions.length > 0) {
    blocks.push(actionBlocks)
  }

  return `${blocks.join(', ')}`
}

const buildUARWorkflowBlocks = (data: WorkflowFormData): string => {
  const uarStateApprovalData = data?.[WorkflowFlowTypes.UAR_STATE_APPROVAL]
  const uarReportGenerationData = data?.[WorkflowFlowTypes.UAR_REPORT_GENERATION]
  const uarScanCompleteNotificationData = data?.[WorkflowFlowTypes.UAR_SCAN_COMPLETE_NOTIFICATION]
  const uarAttestationData = data?.[WorkflowFlowTypes.UAR_ATTESTATION]

  const uarScanCompleteNotificationBlocks = `{
    id: "${uarScanCompleteNotificationData?.id}",
    order: 4,
    type: ${WorkflowBlockType.UAR_SCAN_COMPLETE_NOTIFICATION},
    uarScanCompleteNotifications: {
      enabled: ${uarScanCompleteNotificationData?.enabled},
      recipientGroups: [${uarScanCompleteNotificationData?.recipientGroups}],
      additionalRecipients: ${JSON.stringify(uarScanCompleteNotificationData?.additionalRecipients)}
    }
  }`

  const uarAttestationBlocks = `{
    id: "${uarAttestationData?.id}",
    order: 1,
    type: ${WorkflowBlockType.UAR_ATTESTATION},
    uarAttestationConfig: {
     attestorType: ${uarAttestationData?.attestorType},
     designatedReviewers: ${JSON.stringify(uarAttestationData?.designatedReviewers)}
    }
  }`

  const uarStateApprovalBlocks = `{
    id: "${uarStateApprovalData?.id}",
    order: 2,
    type: ${WorkflowBlockType.UAR_STATE_APPROVAL},
    uarStateApprovals: {
      enabled: ${uarStateApprovalData?.enabled},
      approverGroups: [${uarStateApprovalData?.approverGroups}],
      additionalApprovers: ${JSON.stringify(uarStateApprovalData?.additionalApprovers)},
      states: [${uarStateApprovalData?.states}]
    }
  }`
  const uarReportGenerationBlocks = `{
    id: "${uarReportGenerationData?.id}",
    order: 3,
    type: ${WorkflowBlockType.UAR_REPORT_GENERATION},
    uarReportConfig: {
      enabled: ${uarReportGenerationData?.enabled},
      recipientGroups: [${uarReportGenerationData?.recipientGroups}],
      additionalRecipients: ${JSON.stringify(uarReportGenerationData?.additionalRecipients)}
    }
  }`

  const blocks: string[] = []

  if (uarAttestationData && Object.keys(uarAttestationData).length > 0) {
    blocks.push(uarAttestationBlocks)
  }
  if (uarStateApprovalData && Object.keys(uarStateApprovalData).length > 0) {
    blocks.push(uarStateApprovalBlocks)
  }
  if (uarReportGenerationData && Object.keys(uarReportGenerationData).length > 0) {
    blocks.push(uarReportGenerationBlocks)
  }
  if (uarScanCompleteNotificationData && Object.keys(uarScanCompleteNotificationData).length > 0) {
    blocks.push(uarScanCompleteNotificationBlocks)
  }
  return `${blocks.join(', ')}`
}

export const createWorkflow = (
  name: string,
  type: WorkflowType,
  data: WorkflowFormData
): string => {
  const blocks =
    type === WorkflowType.UAR_WORKFLOW
      ? buildUARWorkflowBlocks(data)
      : buildPolicyWorkflowBlocks(data)

  return gql`
    mutation {
      createFlow(
        clientMutationId: "1"
        inputData: { name: "${name}", type: ${type}, blocks: [${blocks}]}
      ) {
        clientMutationId
        flowId
      }
    }
  `
}

export const updateWorkflow = (
  name: string,
  flowId: string,
  type: WorkflowType,
  data: WorkflowFormData
): string => {
  const blocks =
    type === WorkflowType.UAR_WORKFLOW
      ? buildUARWorkflowBlocks(data)
      : buildPolicyWorkflowBlocks(data)

  return gql`
    mutation {
      updateFlow(
        clientMutationId: "1"
        flowId: "${flowId}"
        inputData: { name: "${name}", type: ${type}, blocks: [${blocks}]}
      ) {
        clientMutationId
        flowId
      }
    }
  `
}

export const deleteWorkflow = (flowId: string, clientMutationId: string): string => {
  return gql`
    mutation {
      deleteFlow(clientMutationId: "${clientMutationId}", flowId: "${flowId}") {
        clientMutationId
      }
    }
  `
}

export const queryWorkflowsTemplate = ({ flowId }: { flowId: string }) => {
  return gql`
    {
      flow(first: 1, id: "${flowId}") {
        edges {
          node {
            id
            name
            type
            createdBy
            createdAt
            blocks {
              id
              order
              type
              config {
                ... on PolicyTemplateUpsertNotificationObject {
                  enabled
                  recipientGroups
                  additionalRecipients
                }
                ...on PolicyAlertAssignmentObject {
                  enabled
                  assignmentGroup
                  additionalRecipients
                }
                ... on PolicyTemplateStateNotificationObject {
                  enabled
                  states
                  recipientGroups
                  additionalRecipients
                  cronExpression
                }
                ... on PolicyTemplateStateApprovalObject {
                  enabled
                  states
                  approverGroups
                  additionalApprovers
                  cronExpression
                }
                ... on PolicyTemplateActionsNotificationsAndApprovalsObject {
                  configs {
                    actionType
                    notificationsEnabled
                    notificationRecipientGroups
                    additionalNotificationRecipients
                    approvalEnabled
                    approverGroups
                    additionalApprovers
                  }
                  cronExpression
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapCreateWorkflow = (raw: any): { flowId: string; clientMutationId: string } => {
  return {
    flowId: raw?.createFlow?.flowId,
    clientMutationId: raw?.createFlow?.clientMutationId
  }
}

export const mapUpdateWorkflow = (raw: any): { flowId: string; clientMutationId: string } => {
  return {
    flowId: raw?.updateFlow?.flowId,
    clientMutationId: raw?.updateFlow?.clientMutationId
  }
}

export const mapDeleteWorkflow = (raw: any): { clientMutationId: string } => {
  return {
    clientMutationId: raw?.deleteFlow?.clientMutationId
  }
}

export const mapQueryWorkflowsTemplate = (raw: any): WorkflowItem => {
  const blocks = raw?.flow?.edges[0]?.node?.blocks || []

  return {
    flowId: raw?.flow?.edges[0]?.node?.id,
    name: raw?.flow?.edges[0]?.node?.name,
    type: raw?.flow?.edges[0]?.node?.type,
    createdBy: raw?.flow?.edges[0]?.node?.createdBy,
    createdAt: raw?.flow?.edges[0]?.node?.createdAt,
    blocks: convertBlocksToWorkflowFormData(blocks)
  }
}

export const queryUARWorkflowsTemplate = ({ flowId }: { flowId: string }) => {
  // todo: test this query
  return gql`
    {
      flow(first: 1, id: "${flowId}") {
        edges {
          node {
            id
            name
            type
            createdBy
            createdAt
            blocks {
              id
              order
              type
              config {
                ... on UARAttestationObject {
                  attestorType
                  designatedReviewers
                }
                ... on UARStateApprovalObject {
                  enabled
                  approverGroups
                  additionalApprovers
                  states
                }
                ... on UARReportGenerationObject {
                  enabled
                  recipientGroups
                  additionalRecipients
                }
                ... on UARScanCompleteNotificationObject {
                  enabled
                  recipientGroups
                  additionalRecipients
                }
              }
            }
        }
      }
    }
  }`
}
export const mapQueryUARWorkflowsTemplate = (raw: any): WorkflowItem => {
  const blocks = raw?.flow?.edges[0]?.node?.blocks || []

  return {
    flowId: raw?.flow?.edges[0]?.node?.id,
    name: raw?.flow?.edges[0]?.node?.name,
    type: raw?.flow?.edges[0]?.node?.type,
    createdBy: raw?.flow?.edges[0]?.node?.createdBy,
    createdAt: raw?.flow?.edges[0]?.node?.createdAt,
    blocks: convertBlocksToUARWorkflowFormData(blocks)
  }
}

export const queryUarWorkflows = () => {
  return gql`
    {
      flow(filter:[{key: ${GRAPHQL_API_FILTERS.type}, values: ["${WorkflowType.UAR_WORKFLOW}"]}] ) {
        edges {
          node {
            id
            name
            type
            createdBy
            createdAt
            blocks {
              id
              order
              type
              config {
                ... on UARAttestationObject {
                  attestorType
                  designatedReviewers
                }
                ... on UARStateApprovalObject {
                  enabled
                  approverGroups
                  additionalApprovers
                  states
                }
                ... on UARReportGenerationObject {
                  enabled
                  recipientGroups
                  additionalRecipients
                }
                ... on UARScanCompleteNotificationObject {
                  enabled
                  recipientGroups
                  additionalRecipients
                }
              }
            }
        }
      }
    }
  }`
}
export const mapQueryUarWorkflows = (raw: any): { list: WorkflowItem[]; total: number } => {
  const list = raw.flow?.edges?.map(({ node }) => {
    const blocks = node?.blocks || []

    return {
      flowId: node?.id,
      name: node?.name || '',
      type: node?.type || '',
      createdBy: node?.createdBy || '',
      createdAt: node?.createdAt || '',
      blocks: convertBlocksToUARWorkflowFormData(blocks)
    }
  })

  return { list, total: raw.flow?.edges?.count || 0 }
}
